/** @typedef {{ id: string, label: string, addOptions: object }} DockPanelDef */

const DOCK_SLOT = 'ControlDesignerDockSlot';

export const DESIGNER_PANEL_ID = 'designer';
export const JSON_PANEL_ID = 'json';
export const DOCUMENT_TREE_PANEL_ID = 'documentTree';
export const CODE_PANEL_ID = 'code';
export const PROPERTIES_PANEL_ID = 'properties';

export const DOCUMENT_USER_CONTROL = '/schema/public/Platform.Schema.Documents.v1/Document_UserControl';

/** Panels that share the center tab group (designer + JSON). */
export const CENTER_TAB_GROUP_PANEL_IDS = new Set([
    DESIGNER_PANEL_ID,
    JSON_PANEL_ID,
]);

/** Panels that share the left tab group (tree + code). */
export const LEFT_TAB_GROUP_PANEL_IDS = new Set([
    DOCUMENT_TREE_PANEL_ID,
    CODE_PANEL_ID,
]);

/** Panels that share the right tab group when JSON is moved beside Properties. */
export const RIGHT_TAB_GROUP_PANEL_IDS = new Set([
    PROPERTIES_PANEL_ID,
    JSON_PANEL_ID,
]);

/** @type {DockPanelDef} */
export const JSON_TAB_PANEL = {
    id: JSON_PANEL_ID,
    label: 'JSON',
    addOptions: {
        id: JSON_PANEL_ID,
        component: DOCK_SLOT,
        title: 'JSON',
        position: { direction: 'within', referencePanel: DESIGNER_PANEL_ID, index: 1 },
        inactive: true,
    },
};

/** @type {DockPanelDef[]} */
export const LEFT_TAB_PANELS = [
    {
        id: DOCUMENT_TREE_PANEL_ID,
        label: 'Outline',
        addOptions: {
            id: DOCUMENT_TREE_PANEL_ID,
            component: DOCK_SLOT,
            title: 'Outline',
            position: { direction: 'left', referencePanel: DESIGNER_PANEL_ID },
        },
    },
    {
        id: CODE_PANEL_ID,
        label: 'Code',
        addOptions: {
            id: CODE_PANEL_ID,
            component: DOCK_SLOT,
            title: 'Code',
            position: { direction: 'within', referencePanel: DOCUMENT_TREE_PANEL_ID, index: 1 },
            inactive: true,
        },
    },
];

/** @type {DockPanelDef[]} */
export const SIDE_DOCK_PANELS = [
    LEFT_TAB_PANELS[0],
    {
        id: PROPERTIES_PANEL_ID,
        label: 'Properties',
        addOptions: {
            id: PROPERTIES_PANEL_ID,
            component: DOCK_SLOT,
            title: 'Properties',
            position: { direction: 'right', referencePanel: DESIGNER_PANEL_ID },
            initialWidth: 320,
        },
    },
];

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 * @returns {import('dockview-core').DockviewGroupPanel | null | undefined}
 */
export function getCenterTabGroup(api) {
    const designerPanel = api?.getPanel?.(DESIGNER_PANEL_ID);
    if (designerPanel?.group)
        return designerPanel.group;
    const jsonPanel = api?.getPanel?.(JSON_PANEL_ID);
    return jsonPanel?.group ?? null;
}

/**
 * Active tab within the center group (Designer vs JSON), not the globally focused dock panel.
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function getCenterActivePanelId(api) {
    const group = getCenterTabGroup(api);
    const activeId = group?.activePanel?.id;
    if (activeId && CENTER_TAB_GROUP_PANEL_IDS.has(activeId))
        return activeId;
    return DESIGNER_PANEL_ID;
}

/** True when the JSON dock tab is selected (center or Properties group). */
export function isJsonTabActive(api) {
    return isPanelActive(api, JSON_PANEL_ID);
}

/** @deprecated Prefer isJsonTabActive; kept for call sites that expect the old name. */
export function isJsonCenterTabActive(api) {
    return isJsonTabActive(api);
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 * @returns {import('dockview-core').DockviewGroupPanel | null | undefined}
 */
export function getPropertiesTabGroup(api) {
    const propertiesPanel = api?.getPanel?.(PROPERTIES_PANEL_ID);
    if (propertiesPanel?.group)
        return propertiesPanel.group;
    const jsonPanel = api?.getPanel?.(JSON_PANEL_ID);
    if (jsonPanel?.group?.panels?.some((p) => p.id === PROPERTIES_PANEL_ID))
        return jsonPanel.group;
    return null;
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 * @returns {import('dockview-core').DockviewGroupPanel | null | undefined}
 */
export function getJsonTabGroup(api) {
    const jsonPanel = api?.getPanel?.(JSON_PANEL_ID);
    return jsonPanel?.group ?? null;
}

/** True when JSON shares a tab group with Properties (Designer hidden). */
export function isJsonInPropertiesGroup(api) {
    const jsonPanel = api?.getPanel?.(JSON_PANEL_ID);
    const propertiesPanel = api?.getPanel?.(PROPERTIES_PANEL_ID);
    if (!jsonPanel?.group || !propertiesPanel?.group)
        return false;
    return jsonPanel.group === propertiesPanel.group;
}

/**
 * Active tab within the group that currently contains JSON.
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function getJsonTabGroupActivePanelId(api) {
    const group = getJsonTabGroup(api);
    return group?.activePanel?.id ?? null;
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function activatePropertiesPanel(api) {
    const panel = api?.getPanel?.(PROPERTIES_PANEL_ID);
    panel?.api?.setActive?.();
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function ensureJsonPanelInPropertiesGroup(api) {
    if (!api?.getPanel)
        return;

    const jsonPanel = api.getPanel(JSON_PANEL_ID);
    const propertiesPanel = api.getPanel(PROPERTIES_PANEL_ID);
    if (!jsonPanel || !propertiesPanel)
        return;
    if (jsonPanel.group === propertiesPanel.group)
        return;

    const wasJsonActive = isPanelActive(api, JSON_PANEL_ID);
    jsonPanel.api.moveTo({
        group: propertiesPanel.group,
        position: 'center',
        index: 1,
        skipSetActive: true,
    });
    if (wasJsonActive)
        jsonPanel.api.setActive?.();
    else
        propertiesPanel.api.setActive?.();
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function ensureJsonPanelInCenterGroup(api) {
    if (!api?.getPanel)
        return;

    const jsonPanel = api.getPanel(JSON_PANEL_ID);
    const designerPanel = api.getPanel(DESIGNER_PANEL_ID);
    if (!jsonPanel || !designerPanel)
        return;
    if (jsonPanel.group === designerPanel.group)
        return;

    const wasJsonActive = isPanelActive(api, JSON_PANEL_ID);
    jsonPanel.api.moveTo({
        group: designerPanel.group,
        position: 'center',
        index: 1,
        skipSetActive: true,
    });
    if (wasJsonActive)
        jsonPanel.api.setActive?.();
    else
        designerPanel.api.setActive?.();
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 * @returns {import('dockview-core').DockviewGroupPanel | null | undefined}
 */
export function getLeftTabGroup(api) {
    const treePanel = api?.getPanel?.(DOCUMENT_TREE_PANEL_ID);
    if (treePanel?.group)
        return treePanel.group;
    const codePanel = api?.getPanel?.(CODE_PANEL_ID);
    return codePanel?.group ?? null;
}

/**
 * Active tab within the left group (Outline vs Code), not the globally focused dock panel.
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function getLeftActivePanelId(api) {
    const group = getLeftTabGroup(api);
    const activeId = group?.activePanel?.id;
    if (activeId && LEFT_TAB_GROUP_PANEL_IDS.has(activeId))
        return activeId;
    return DOCUMENT_TREE_PANEL_ID;
}

/** True when the Code panel is the selected tab in its dock group (any group). */
export function isCodePanelActive(api) {
    return isPanelActive(api, CODE_PANEL_ID);
}

/** True when the Properties panel is the selected tab in its dock group (any group). */
export function isPropertiesPanelActive(api) {
    return isPanelActive(api, PROPERTIES_PANEL_ID);
}

/**
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 * @param {string} panelId
 */
export function isPanelActive(api, panelId) {
    const panel = api?.getPanel?.(panelId);
    if (!panel?.group)
        return false;
    if (panel.api?.isVisible === false)
        return false;
    return panel.group.activePanel?.id === panelId;
}

/** @type {DockPanelDef} */
export const PROPERTIES_TAB_PANEL = SIDE_DOCK_PANELS[1];

/**
 * When Code must be deactivated, activate another tab in the same group, or designer/documentTree.
 * @param {import('dockview-core').DockviewApi | null | undefined} api
 */
export function activateSiblingOfCodePanel(api) {
    if (!api?.getPanel)
        return;

    const codePanel = api.getPanel(CODE_PANEL_ID);
    if (codePanel?.group?.activePanel?.id === CODE_PANEL_ID) {
        const sibling = codePanel.group.panels.find((p) => p.id !== CODE_PANEL_ID);
        if (sibling?.api?.setActive) {
            sibling.api.setActive();
            return;
        }
    }

    const designerPanel = api.getPanel(DESIGNER_PANEL_ID);
    if (designerPanel?.api?.setActive) {
        designerPanel.api.setActive();
        return;
    }

    const treePanel = api.getPanel(DOCUMENT_TREE_PANEL_ID);
    treePanel?.api?.setActive?.();
}

/**
 * @param {import('dockview-core').IDockviewPanel | undefined | null} panel
 */
export function isGroupVisible(panel) {
    return panel?.group?.api?.isVisible !== false;
}

/**
 * @param {import('dockview-core').IDockviewPanel | undefined | null} panel
 * @param {boolean} visible
 */
export function setGroupVisible(panel, visible) {
    if (!panel?.group?.api) return;
    if (typeof panel.group.api.setVisible === 'function') {
        panel.group.api.setVisible(visible);
    }
}

/**
 * Default ControlDesigner dock layout (designer center + tree left + properties right).
 * @param {import('dockview-core').DockviewApi} api
 * @param {{ showTree?: boolean, treeWidth?: number }} [options]
 */
export function buildControlDesignerDefaultLayout(api, options = {}) {
    const { showTree = true, treeWidth = 300 } = options;

    api.addPanel({
        id: DESIGNER_PANEL_ID,
        component: DOCK_SLOT,
        title: 'Designer',
    });
    api.addPanel(JSON_TAB_PANEL.addOptions);

    if (showTree) {
        api.addPanel({
            ...LEFT_TAB_PANELS[0].addOptions,
            initialWidth: treeWidth,
        });
        api.addPanel(LEFT_TAB_PANELS[1].addOptions);
    }

    api.addPanel(PROPERTIES_TAB_PANEL.addOptions);
}
